import { useEffect, useRef } from "react";

const DEEP_LINK_DEDUPE_MS = 1000;

/**
 * Navigates the SPA when the Electron shell delivers an illumi:// deep link
 * (cold start via getInitialDeepLink, or runtime via onDeepLink).
 */
export function useDesktopDeepLinkNavigation(
	navigate: (to: string) => void | Promise<void>,
) {
	const navigateRef = useRef(navigate);
	navigateRef.current = navigate;

	const lastNavRef = useRef<{ path: string; at: number } | null>(null);

	useEffect(() => {
		const api = window.electronAPI;
		if (!api?.isElectron) return;

		let isMounted = true;

		function navigateDeepLink(routePath: string) {
			if (!routePath) return;
			const now = Date.now();
			const last = lastNavRef.current;
			if (
				last &&
				last.path === routePath &&
				now - last.at < DEEP_LINK_DEDUPE_MS
			) {
				return;
			}
			lastNavRef.current = { path: routePath, at: now };
			void navigateRef.current(routePath);
		}

		api
			.getInitialDeepLink?.()
			.then((initial) => {
				if (!isMounted || !initial?.routePath) return;
				navigateDeepLink(initial.routePath);
			})
			.catch((err) => {
				console.warn("Failed to get initial deep link:", err);
			});

		const unsubscribe = api.onDeepLink?.((payload) => {
			if (!payload?.routePath) return;
			navigateDeepLink(payload.routePath);
		});

		return () => {
			isMounted = false;
			unsubscribe?.();
		};
	}, []);
}
